import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { supabase } from '../supabaseClient'
import { useUser } from '../contexts/UserContext'
import { useNavigate } from 'react-router-dom'

export default function SuperAdmin() {
  const { userData, userId } = useUser()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('users')
  const [usuarios, setUsuarios] = useState([])
  const [workspaces, setWorkspaces] = useState([])
  const [saques, setSaques] = useState([])
  const [saquesAfiliados, setSaquesAfiliados] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('')
  const [modalComprovante, setModalComprovante] = useState(null)
  const [uploading, setUploading] = useState(false)

  // Verificar se é superadmin
  useEffect(() => {
    if (userData && !userData.is_superadmin) {
      navigate('/app/home')
    }
  }, [userData, navigate])

  useEffect(() => {
    if (userData?.is_superadmin) {
      carregarDados()
    }
  }, [userData, activeTab])

  const carregarDados = async () => {
    setLoading(true)
    try {
      if (activeTab === 'users') {
        const { data } = await supabase
          .from('usuarios')
          .select('id, email, nome_completo, nome_empresa, plano, plano_pago, is_superadmin, created_at, status_conta')
          .order('created_at', { ascending: false })
        setUsuarios(data || [])
      } else if (activeTab === 'workspaces') {
        const { data } = await supabase
          .from('workspaces')
          .select('*, usuarios(nome_empresa, email)')
          .order('created_at', { ascending: false })
        setWorkspaces(data || [])
      } else if (activeTab === 'saques') {
        const { data, error } = await supabase
          .from('saques_agendados')
          .select('*, usuarios(nome_empresa, email), workspaces(nome)')
          .order('created_at', { ascending: false })
          .limit(100)
        if (error) {
          console.error('Erro ao carregar saques:', error)
          setSaques([])
        } else {
          setSaques(data || [])
        }
      } else if (activeTab === 'saques-afiliados') {
        const { data, error } = await supabase
          .from('saques_afiliados')
          .select('*, usuarios(nome_empresa, email, cnpj)')
          .order('data_solicitacao', { ascending: false })
          .limit(100)
        if (error) {
          console.error('Erro ao carregar saques afiliados:', error)
          setSaquesAfiliados([])
        } else {
          setSaquesAfiliados(data || [])
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleSuperAdmin = async (userId, currentStatus) => {
    try {
      await supabase
        .from('usuarios')
        .update({ is_superadmin: !currentStatus })
        .eq('id', userId)
      carregarDados()
    } catch (error) {
      console.error('Erro ao atualizar:', error)
    }
  }

  const processarSaqueAfiliado = async (saqueId, status) => {
    try {
      await supabase
        .from('saques_afiliados')
        .update({ 
          status,
          data_processamento: status === 'pago' ? new Date().toISOString() : null,
          processado_por: userData.id
        })
        .eq('id', saqueId)
      carregarDados()
    } catch (error) {
      console.error('Erro ao processar saque:', error)
      alert('Erro ao processar saque')
    }
  }

  const uploadComprovante = async (saqueId, file) => {
    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `comprovante-${saqueId}-${Date.now()}.${fileExt}`
      
      const { error: uploadError } = await supabase
        .storage
        .from('comprovantes-saques')
        .upload(fileName, file)
      
      if (uploadError) throw uploadError
      
      const { data: { publicUrl } } = supabase
        .storage
        .from('comprovantes-saques')
        .getPublicUrl(fileName)
      
      await supabase
        .from('saques_afiliados')
        .update({ 
          comprovante_url: publicUrl,
          status: 'pago',
          data_processamento: new Date().toISOString(),
          processado_por: userData.id
        })
        .eq('id', saqueId)
      
      setModalComprovante(null)
      carregarDados()
      alert('Comprovante enviado com sucesso!')
    } catch (error) {
      console.error('Erro ao fazer upload:', error)
      alert('Erro ao enviar comprovante')
    } finally {
      setUploading(false)
    }
  }

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  if (!userData?.is_superadmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Icon icon="mdi:lock" width="48" style={{ color: '#ef4444', marginBottom: '16px' }} />
        <h2>Acesso Negado</h2>
        <p>Você não tem permissão para acessar esta área.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon icon="mdi:shield-crown" width="32" style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
              Painel Super Admin
            </h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 0' }}>
              Controle total do sistema
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
        {[
          { id: 'users', label: 'Usuários', icon: 'mdi:account-group' },
          { id: 'workspaces', label: 'Workspaces', icon: 'mdi:office-building' },
          { id: 'saques', label: 'Saques Clientes', icon: 'mdi:cash-clock' },
          { id: 'saques-afiliados', label: 'Saques Afiliados', icon: 'mdi:hand-coin' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              borderRadius: '10px',
              border: 'none',
              background: activeTab === tab.id ? '#7c3aed' : '#f1f5f9',
              color: activeTab === tab.id ? 'white' : '#64748b',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Icon icon={tab.icon} width="18" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtro */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Icon icon="mdi:magnify" width="20" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Buscar..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 44px',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Icon icon="mdi:loading" width="32" className="spin" />
          <p>Carregando...</p>
        </div>
      ) : activeTab === 'users' ? (
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Usuário</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Plano</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Super Admin</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Cadastro</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.filter(u => 
                u.email?.toLowerCase().includes(filtro.toLowerCase()) ||
                u.nome_empresa?.toLowerCase().includes(filtro.toLowerCase())
              ).map(user => (
                <tr key={user.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '50%',
                        background: user.is_superadmin ? '#7c3aed' : '#e2e8f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: user.is_superadmin ? 'white' : '#64748b',
                        fontWeight: '600'
                      }}>
                        {user.nome_empresa?.charAt(0) || user.email?.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{user.nome_empresa || 'Sem empresa'}</div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                      background: user.plano === 'premium' ? '#f3e8ff' : user.plano === 'pro' ? '#dbeafe' : '#f1f5f9',
                      color: user.plano === 'premium' ? '#7c3aed' : user.plano === 'pro' ? '#2563eb' : '#64748b'
                    }}>
                      {user.plano?.toUpperCase() || 'STARTER'}
                    </span>
                    {user.plano_pago && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#16a34a' }}>
                        <Icon icon="mdi:check-circle" width="12" /> Pago
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={() => toggleSuperAdmin(user.id, user.is_superadmin)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: user.is_superadmin ? '#7c3aed' : '#f1f5f9',
                        color: user.is_superadmin ? 'white' : '#64748b',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {user.is_superadmin ? 'Sim' : 'Não'}
                    </button>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                    {formatDate(user.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'workspaces' ? (
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Workspace</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Owner</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Asaas</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Criado</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.filter(w => 
                w.nome?.toLowerCase().includes(filtro.toLowerCase())
              ).map(ws => (
                <tr key={ws.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{ws.nome}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{ws.cnpj || 'Sem CNPJ'}</div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#1e293b' }}>{ws.usuarios?.nome_empresa}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>{ws.usuarios?.email}</div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {ws.asaas_account_id ? (
                      <span style={{ color: '#16a34a', fontSize: '13px' }}>
                        <Icon icon="mdi:check-circle" width="16" /> Conectado
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '13px' }}>Não</span>
                    )}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                    {formatDate(ws.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'saques' ? (
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Usuário</th>
                <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Valor</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Data do Saque</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Chave PIX</th>
              </tr>
            </thead>
            <tbody>
              {saques.filter(s => 
                s.usuarios?.nome_empresa?.toLowerCase().includes(filtro.toLowerCase())
              ).map(saque => (
                <tr key={saque.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{saque.usuarios?.nome_empresa}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{saque.usuarios?.email}</div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                    {formatCurrency(saque.valor)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                    {formatDate(saque.data_saque)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                      background: saque.status === 'processado' ? '#dcfce7' : saque.status === 'falhou' ? '#fef2f2' : '#fef9c3',
                      color: saque.status === 'processado' ? '#166534' : saque.status === 'falhou' ? '#dc2626' : '#854d0e'
                    }}>
                      {saque.status === 'processado' ? 'Processado' : saque.status === 'falhou' ? 'Falhou' : 'Pendente'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px' }}>
                    <span style={{ color: '#64748b' }}>{saque.pix_tipo}:</span>
                    <br />
                    <span style={{ fontWeight: '500' }}>{saque.pix_chave}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Afiliado</th>
                <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Valor</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Período</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {saquesAfiliados.filter(s => 
                s.usuarios?.nome_empresa?.toLowerCase().includes(filtro.toLowerCase())
              ).map(saque => (
                <tr key={saque.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{saque.usuarios?.nome_empresa}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{saque.usuarios?.email}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      CNPJ: {saque.usuarios?.cnpj || 'Não cadastrado'}
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                    {formatCurrency(saque.valor)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                    {formatDate(saque.periodo_inicio)} - {formatDate(saque.periodo_fim)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                      background: saque.status === 'pago' ? '#dcfce7' : saque.status === 'rejeitado' ? '#fef2f2' : '#fef9c3',
                      color: saque.status === 'pago' ? '#166534' : saque.status === 'rejeitado' ? '#dc2626' : '#854d0e'
                    }}>
                      {saque.status === 'pago' ? 'Pago' : saque.status === 'rejeitado' ? 'Rejeitado' : 'Pendente'}
                    </span>
                    {saque.comprovante_url && (
                      <div style={{ marginTop: '6px' }}>
                        <a href={saque.comprovante_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#7c3aed' }}>
                          <Icon icon="mdi:file-check" width="12" /> Ver comprovante
                        </a>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {saque.status === 'pendente' && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => setModalComprovante(saque)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#16a34a',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <Icon icon="mdi:check" width="14" /> Pagar
                        </button>
                        <button
                          onClick={() => processarSaqueAfiliado(saque.id, 'rejeitado')}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#dc2626',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <Icon icon="mdi:close" width="14" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal de Upload de Comprovante */}
        {modalComprovante && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '500px',
              margin: '20px'
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>
                Confirmar Pagamento
              </h3>
              <p style={{ color: '#64748b', margin: '0 0 24px 0' }}>
                {modalComprovante.usuarios?.nome_empresa} - {formatCurrency(modalComprovante.valor)}
              </p>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Chave PIX (CNPJ):
                </label>
                <div style={{
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}>
                  {modalComprovante.usuarios?.cnpj || 'CNPJ não cadastrado'}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Comprovante de Transferência:
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      uploadComprovante(modalComprovante.id, e.target.files[0])
                    }
                  }}
                  style={{ display: 'none' }}
                  id="comprovante-upload"
                />
                <label
                  htmlFor="comprovante-upload"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '16px',
                    border: '2px dashed #cbd5e1',
                    borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.6 : 1
                  }}
                >
                  {uploading ? (
                    <><Icon icon="mdi:loading" width="20" className="spin" /> Enviando...</>
                  ) : (
                    <><Icon icon="mdi:upload" width="20" /> Clique para anexar comprovante</>
                  )}
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setModalComprovante(null)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  )
}
